import { useMemo, type FC } from "react";
import { Chart } from "primereact/chart";
import { Tooltip } from "chart.js";
import styled from "styled-components";

import { stringToHSLCSS } from "@ds/utils/color";

import { getInterleavedSequence, getTimeSliceSequence } from "../../utils/helpers";

// Register a custom positioner that pins the tooltip caret to y=25 (above the bar).
// Cast avoids needing a module augmentation declaration that linters tend to strip.
(Tooltip.positioners as unknown as Record<string, unknown>).aboveBar = (
  _items: unknown,
  eventPosition: { x: number; y: number },
) => ({ x: eventPosition.x, y: 25 });

const ChartWrapper = styled.div`
  position: relative;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ChartTitle = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const BarWrapper = styled.div`
  height: 80px;

  .p-chart {
    height: 92px;

    canvas {
      height: 30px;
    }
  }
`;

const EmptyLabel = styled.p`
  color: var(--textColor060);
  font-size: 0.85rem;
  margin: 0;
`;

const BAR_OPTIONS = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      position: "aboveBar",
      yAlign: "bottom",
      callbacks: {
        label: (ctx: { dataset: { label?: string }; raw: unknown }) => ` ${ctx.dataset.label}`,
      },
    },
  },
};

export const CampaignItemWeightVisualization: FC<{
  items: CampaignItem[];
  mixingStrategy: Campaign["mixing_strategy"];
}> = ({ items, mixingStrategy }) => {
  const isTimeSlice = mixingStrategy === "time_slice";

  const barItems = useMemo(
    () => (isTimeSlice ? getTimeSliceSequence(items ?? []) : getInterleavedSequence(items ?? [])),
    [items, isTimeSlice],
  );

  const barData = useMemo(() => {
    return {
      labels: [""],
      datasets: barItems.map((item, i) => ({
        label: `${item.playlist_name ?? `Playlist ${i + 1}`}` + (isTimeSlice ? `: ${item.weight}%` : ""),
        data: isTimeSlice ? [item.weight] : [(item as { slot: number }).slot],
        backgroundColor: stringToHSLCSS(item.playlist_name ?? " "),
        hoverBackgroundColor: stringToHSLCSS(item.playlist_name ?? " "),
        borderColor: "transparent",
        borderWidth: 1,
        barThickness: 30,
      })),
    };
  }, [barItems, isTimeSlice]);

  const barOptions = useMemo(() => {
    const xMax = isTimeSlice ? 100 : barItems.reduce((s, item) => s + item.slot, 0) || 100;
    return {
      ...BAR_OPTIONS,
      scales: {
        x: { stacked: true, max: xMax, display: false },
        y: { stacked: true, display: false },
      },
    };
  }, [isTimeSlice, barItems]);

  if (!items?.length) {
    return <EmptyLabel>No playlists to display.</EmptyLabel>;
  }

  const chartDescription = isTimeSlice
    ? "With Time Slice, Playlists are grouped in blocks, and weights control screen time. This is best for ensuring proportional screen time across a full content cycle."
    : "Interleaved distributes slots as evenly as possible given the weights. This is best for ensuring a variety of content is visible immediately.";

  return (
    <ChartWrapper>
      <ChartHeader>
        <ChartTitle>{isTimeSlice ? "Time Slice" : "Interleaved"}</ChartTitle>
        <small style={{ color: "var(--textColor060)", maxWidth: "40%" }}>{chartDescription}</small>
      </ChartHeader>
      <BarWrapper>
        <Chart type="bar" data={barData} options={barOptions} />
      </BarWrapper>
    </ChartWrapper>
  );
};
